import * as React from "react";
import { track } from "@/lib/track";
import { trackLandingCta } from "@/lib/track-landing-cta";

const PAGE_VARIANT = "homepage_trial_v2";

/**
 * Mirrors the reference homepage script: broadcast a window CustomEvent and,
 * only when a dataLayer array already exists, push the same payload. We never
 * create window.dataLayer ourselves.
 */
export function emitInteraction(event: string, fields: Record<string, unknown>): void {
  if (typeof window === "undefined") return;
  const detail = { event, page_variant: PAGE_VARIANT, ...fields };
  try {
    window.dispatchEvent(new CustomEvent("capitaldart:interaction", { detail }));
  } catch {
    /* Analytics must never break the UI. */
  }
  try {
    const dataLayer = (window as unknown as { dataLayer?: unknown }).dataLayer;
    if (Array.isArray(dataLayer)) dataLayer.push(detail);
  } catch {
    /* Analytics must never break the UI. */
  }
}

function visibleText(el: Element): string {
  return (el.textContent ?? "").replace(/\s+/g, " ").trim();
}

/**
 * Single React click handler for the homepage root. Every element carrying
 * data-cta or data-evidence reports once per click; delegation keeps handlers
 * from accumulating across route navigations.
 */
export function handleHomeClick(evt: React.MouseEvent<HTMLElement>): void {
  const target = evt.target as Element | null;
  if (!target || typeof target.closest !== "function") return;

  const demo = target.closest("[data-demo-placement]");
  if (demo) {
    trackDemoStart(demo.getAttribute("data-demo-placement") ?? "demo-section");
    return;
  }

  const cta = target.closest("[data-cta]");
  if (cta) {
    const placement = cta.getAttribute("data-cta") ?? "";
    const label = visibleText(cta);
    trackLandingCta(placement, label);
    emitInteraction("landing_cta_click", { cta_placement: placement, cta_label: label });
    return;
  }

  const evidence = target.closest("[data-evidence]");
  if (evidence) {
    const evidenceType = evidence.getAttribute("data-evidence") ?? "";
    track("evidence_open", { evidence_type: evidenceType });
    emitInteraction("evidence_open", { evidence_type: evidenceType });
  }
}

export function trackDemoStart(ctaPlacement: string): void {
  track("demo_start", { cta_placement: ctaPlacement });
  emitInteraction("demo_start", { cta_placement: ctaPlacement });
}

/**
 * Sticky header compaction. Two thresholds (8 down, 80 up) are the
 * anti-flicker mechanism from the reference script and are kept as-is.
 */
export function useHeaderCompaction(
  headerRef: React.RefObject<HTMLElement | null>,
  credentialsRef: React.RefObject<HTMLElement | null>,
): void {
  React.useEffect(() => {
    let compact = false;
    let frame = 0;

    const apply = () => {
      frame = 0;
      const next = compact ? window.scrollY > 8 : window.scrollY > 80;
      if (next === compact) return;
      compact = next;
      headerRef.current?.classList.toggle("is-compact", compact);
      credentialsRef.current?.setAttribute("aria-hidden", String(compact));
    };

    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(apply);
    };

    apply();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("pageshow", onScroll);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("pageshow", onScroll);
    };
  }, [headerRef, credentialsRef]);
}

/**
 * The mobile trial bar hides whenever the hero or the closing section is on
 * screen, matching the reference observer. Without IntersectionObserver the
 * bar stays hidden.
 */
export function useMobileTrialVisibility(): boolean {
  const [heroVisible, setHeroVisible] = React.useState(true);
  const [closingVisible, setClosingVisible] = React.useState(false);
  const [supported, setSupported] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === "undefined" || !("IntersectionObserver" in window)) return;
    const hero = document.querySelector(".cd-home .hero");
    const closing = document.querySelector(".cd-home .closing");
    if (!hero && !closing) return;
    setSupported(true);

    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === hero) setHeroVisible(entry.isIntersecting);
        if (entry.target === closing) setClosingVisible(entry.isIntersecting);
      }
    });
    if (hero) observer.observe(hero);
    if (closing) observer.observe(closing);
    return () => observer.disconnect();
  }, []);

  if (!supported) return false;
  return !heroVisible && !closingVisible;
}
